import { Button, Group, Select, Text, Title } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  axiosFieldValidationErrorToFormErrors,
  EncryptionMode,
  type HttpNewForm,
  hasFieldValidationError,
  LOGIN_METHOD,
} from '@/api';
import { parseApiError } from '@/api/utils/notifications';
import { clearField } from '../../lib/clear-field';
import { FORM_TEMPLATES, type FormTemplate } from '../../lib/form-templates';
import { useRustyState } from '../../state';
import { FormFields, type FormFieldsValues } from './Common';

export interface CreateFormProps {
  submitFormCb: (newForm: HttpNewForm) => Promise<void>;
  template?: FormTemplate;
  onTemplateChange?: (templateId: string | null) => void;
}

export function CreateForm(props: CreateFormProps) {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const session = useRustyState((state) => state.getSession());

  // Check if user has Nostr pubkey (authenticated via Nostr)
  const hasNostrPubkey =
    session?.isLoggedIn &&
    (session.method === LOGIN_METHOD.NOSTR || session.method === LOGIN_METHOD.MANUAL) &&
    'publicKey' in session &&
    !!session.publicKey;

  // Derive max_retention_days from the user's current subscription plan
  const { subscriptionPlans, currentSubscription } = useRustyState();
  const maxRetentionDays = useMemo(() => {
    if (!subscriptionPlans || !currentSubscription) return 30; // default to free tier
    const plan = subscriptionPlans.find((p) => p.id === currentSubscription.plan_id);
    return plan?.max_retention_days ?? null; // null means unlimited
  }, [subscriptionPlans, currentSubscription]);

  const initialValues: FormFieldsValues = useMemo(
    () => ({
      title: props.template ? t(props.template.titleKey) : '',
      filter_spam: false,
      check_challenge: false,
      redirect_url: undefined,
      auto_response_enabled: false,
      encryption_mode: EncryptionMode.None,
      retention_days: maxRetentionDays,
      allowed_origins: [],
      ...(props.template?.formDefaults ?? {}),
    }),
    [props.template, t, maxRetentionDays]
  );

  const formDataRef = useRef<FormFieldsValues>({ ...initialValues });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [resetKey, setResetKey] = useState(0);

  // When the template changes, reset the form to the new template's defaults.
  useEffect(() => {
    formDataRef.current = { ...initialValues };
    setResetKey((k) => k + 1);
  }, [initialValues]);

  const handleFormFieldsChange = useCallback((values: Partial<FormFieldsValues>) => {
    Object.assign(formDataRef.current, values);
  }, []);

  const submitForm = async () => {
    const newForm: HttpNewForm = {
      title: formDataRef.current.title,
      filter_spam: formDataRef.current.filter_spam,
      check_challenge: formDataRef.current.check_challenge,
      redirect_url: clearField(formDataRef.current.redirect_url),
      encryption_mode: formDataRef.current.encryption_mode,
      retention_days: formDataRef.current.retention_days,
      allowed_origins: formDataRef.current.allowed_origins,
    };
    setIsBusy(true);
    try {
      await props.submitFormCb(newForm);
      setError(null);
    } catch (e) {
      setError({
        ...parseApiError(e),
      });
      if (hasFieldValidationError(e)) {
        setFieldErrors(axiosFieldValidationErrorToFormErrors(e));
      }
    } finally {
      setIsBusy(false);
    }
  };

  const templateOptions = useMemo(
    () => [
      { value: '', label: t('forms.template.none') },
      ...FORM_TEMPLATES.map((tpl) => ({
        value: tpl.id,
        label: t(tpl.titleKey),
      })),
    ],
    [t]
  );

  return (
    <>
      <Title order={3}>{t('forms.createForm')}</Title>
      <Text>{t('forms.createDescription')}</Text>
      {props.onTemplateChange && (
        <Select
          mt="sm"
          mb="md"
          label={t('forms.template.selectedTemplate')}
          description={t('forms.template.selectedTemplateDescription')}
          data={templateOptions}
          value={props.template?.id ?? ''}
          onChange={(value) => props.onTemplateChange?.(value || null)}
          allowDeselect={false}
        />
      )}
      <FormFields
        isEditing={false}
        isBusy={isBusy}
        hasError={error}
        fieldErrors={fieldErrors}
        submitCb={submitForm}
        formSubmitUrl="https://formshive.com/TBD"
        initialValues={initialValues}
        onValuesChange={handleFormFieldsChange}
        hasUnsavedChanges={false}
        hasNostrPubkey={hasNostrPubkey}
        maxRetentionDays={maxRetentionDays}
        resetKey={resetKey}
        onCancel={() => {
          console.log('Cancel clicked');
        }}
      />
      <Group justify="flex-end" mt="md">
        <Button loading={isBusy} onClick={submitForm} leftSection={<IconDeviceFloppy size={16} />}>
          {t('forms.createForm')}
        </Button>
      </Group>
    </>
  );
}
